import { forwardRef, type AnchorHTMLAttributes } from 'react';
import { Link } from 'react-router-dom';

interface CtaLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
}

/**
 * A section CTA's destination (MYN-255's Hero/legal-experience bands link to
 * an in-page anchor, `#apply`, not a route). React Router's `Link` updates
 * the URL hash but never scrolls to it - only a plain `<a href="#...">`
 * triggers the browser's native scroll-to-element behavior. Every other
 * `ctaUrl` (a real route) still goes through `Link`, so client-side
 * navigation for those is unaffected.
 *
 * Due Diligence Platform's `mailto:` CTAs (docs/specs/features/public-pages/
 * 02-design.md §23, FR-154/FR-157, A-134) need the same plain-`<a>` treatment
 * as `#` - react-router's `Link` resolves a bare string `to` as an internal
 * path relative to the current route, which would mangle a `mailto:`/`tel:`
 * address rather than opening the user's mail/phone app. `http(s):` is
 * included for the same reason, for any future external CTA.
 *
 * Forwards ref and spreads every other prop through (className, etc.) -
 * every call site renders this as `<Button asChild>`'s child, and Radix
 * Slot clones the button's own props onto it; without forwarding them the
 * button's styling/behavior would silently never reach the actual element.
 */
const EXTERNAL_HREF_PATTERN = /^(#|mailto:|tel:|https?:\/\/)/;

export const CtaLink = forwardRef<HTMLAnchorElement, CtaLinkProps>(function CtaLink(
  { to, children, ...props },
  ref,
) {
  if (EXTERNAL_HREF_PATTERN.test(to)) {
    return (
      <a ref={ref} href={to} {...props}>
        {children}
      </a>
    );
  }
  return (
    <Link ref={ref} to={to} {...props}>
      {children}
    </Link>
  );
});
